import Component from "./component";
import * as CreativeUtils from "../creative_utils";
import * as ClickHandling from "../click_handling";
import LiftoffMRAID from "./liftoff_mraid";
import { InteractionMonitor } from "../interaction_monitor";
import LetterboxConfig from "./letterbox_config";
import Logger from "./logger";
import WindowResizeLogger from "./window_resize_logger";
import {
  AUDIO_POLICY_NO_RESTRICTIONS,
  CLICK_METRIC_TYPE_CLOSE,
  CLICK_METRIC_TYPE_CTA,
  CLICK_METRIC_TYPE_DEADZONE,
  CLICK_METRIC_TYPE_UNKNOWN,
  CLICK_OPEN_METHOD_MRAID,
} from "../constants";
import * as DOMUtils from "../frontendlib/dom_utils";
import * as Utils from "../frontendlib/utils";
import { clickAreaTypeFromEvent, pointInCTA } from "./cta";
import { getAdexpContextSnapshotFn } from "./adexp";
import Autoclick from "./autoclick/Autoclick";
import Madlib from "./madlib";
import LastChanceEndscreen from "../lib/last_chance_endscreen/last_chance_endscreen";
import { MESSAGE_TRESENSA_INTERACTION } from "./csp-env/messages";

/**
 * NOTE(oscar): After 3 seconds, log what mraid methods/events have been
 * supported. This should be right before users typically close the ad
 * which should give enough time to check what events have triggered or
 * methods used.
 */
const logMraidSupport = () => {
  setTimeout(() => {
    Logger.recordLog(
      "mraid",
      // TODO(oscar): Use the data param once adexp logs can save it.
      JSON.stringify({
        tag: "mraidSupport",
        ...LiftoffMRAID.getSupportedLog(),
      })
    );
  }, 3000);
};

/**
 * Function used to handle auto-endcard interaction event from TreSensa
 * creatives made from the "tresensa-snippet" Madlib component.
 * @param {*} e
 * @param {*} autoEndcard
 * @param {*} lastChanceEndscreenAutoclick
 * @param {*} lastChanceEndscreenInteractionAutoclick
 * TODO(luis): We need to integrate TreSensa interaction events with the
 * InteractionMonitor class.
 */
const onTreSensaInteraction = (
  e,
  autoEndcard,
  lastChanceEndscreenAutoclick,
  lastChanceEndscreenInteractionAutoclick,
  vxInteractionListeners
) => {
  if (e.data === MESSAGE_TRESENSA_INTERACTION) {
    if (lastChanceEndscreenAutoclick) {
      lastChanceEndscreenAutoclick.disable();
    }
    // TODO(Luis): This is a temporary fix to handle the case where the
    // interaction event is triggered from a TreSensa wrapped 3p.
    // Eventually this needs to be deleted and updated so that a
    // tre_interaction event does trigger a global interaction that can be
    // detected by the AutoClick interaction behaviour.
    if (
      autoEndcard &&
      lastChanceEndscreenInteractionAutoclick &&
      lastChanceEndscreenInteractionAutoclick._behavior &&
      lastChanceEndscreenInteractionAutoclick._behavior.delay
    ) {
      // Display auto-endcard after a delay on interaction.
      setTimeout(() => {
        autoEndcard.initAutoEndcard();
      }, lastChanceEndscreenInteractionAutoclick._behavior.delay);
    }

    // NOTE(Sangeeth): The onTreSensaInteraction handler is only called for
    // tresensa client wrapped playables. These playables run inside an iframe
    // so we can't detect interaction events for them through the interaction
    // monitor.
    vxInteractionListeners.forEach((cb) => cb());
  }
};

class Liftoff {
  static init(env, assets, trackingTags = []) {
    try {
      /** TODO(oscar): Consider logging MRAID support for CSP creatives. */
      logMraidSupport();

      if (trackingTags != null) {
        LiftoffMRAID.onViewable(() => {
          const trackingTagHolder = document.getElementById(
            "tracking-tag-holder"
          );
          DOMUtils.appendImpressionTags(trackingTagHolder, trackingTags);
        });
      }

      this.env = env;
      this.populateAppStoreData();
      // The bid context is often used, giving it an alias saves characters and lines of this JS.
      this.bidContext = env.bid_context;
      this.assets = assets;
      this.testGroups = {};
      this.bidContext.ab_tests.forEach((group) => {
        this.testGroups[group] = true;
      });
      this.lastClickMetric = null;
      this.resetClickParams();
      this.interactionMonitor = new InteractionMonitor();
      this.vxInteractionListeners = [];
      this.interactionMonitor.onInteractionChange((e) => {
        this.vxInteractionListeners.forEach((cb) => cb(e));
        this._autoclickBus.forEach((ac) => ac.emitInteractionEvent(e));
      });

      this.snippetEl = null;
      this.updateMRAID();
      this.loadDOM();
      this.loadLogger();
      this.initAutoclickBehaviors();
      Logger.recordMetricV2("initial_load_count", 1);
      this.applyPolicy();
      this.windowResizeLogger = new WindowResizeLogger(
        this.snippetEl,
        this.env.enable_logging
      );
      this.addEventListeners();
      this.madlibLoaded = false;
      this.useAutoendcardBehaviour = null;

      LiftoffMRAID.on("error", (e) => Logger.recordError(e));
    } catch (e) {
      Logger.recordError(e);
    }
  }

  static populateAppStoreData() {
    if (!this.env.app_store_data) {
      try {
        if (window.parent.VungleHelper) {
          const tokens = window.parent.VungleHelper.tokens;
          if (tokens) {
            this.env.app_store_data = {
              icon: tokens.APP_ICON,
              name: tokens.APP_NAME,
              description: tokens.APP_DESCRIPTION,
              rating: tokens.APP_STORE_RATING,
            };
          }
        }
      } catch (error) {
        console.error("Error occurred:", error.message);
      }
    }
  }

  static initAutoclickBehaviors() {
    this._autoclickBus = [];
    LiftoffMRAID.on("clickMethodTried", ({ clickOpenMethod, isAutoclick }) => {
      if (isAutoclick && clickOpenMethod === CLICK_OPEN_METHOD_MRAID) {
        Logger.recordMetricV2(
          "mraid_autoclick_fired_ms",
          Date.now() - LIFTOFF_START_TS
        );
      }
    });

    this._viewclick = new Autoclick(this.bidContext.viewclick_behavior, {
      clickthroughHandler: (lastEvent) =>
        this.handleClick(
          {
            mac: 1,
          },
          clickAreaTypeFromEvent({
            clientX:
              lastEvent == null ? null : lastEvent.lastInteraction.startX,
            clientY:
              lastEvent == null ? null : lastEvent.lastInteraction.startY,
          })
        ),
    });
    this._autoclickBus.push(this._viewclick);

    // We store app store data in this variable for reusability.
    const appStoreData = this.env.app_store_data;

    let autoEndcard;
    // Only initialize autoEndcard if there is app store data in the bid context.
    if (appStoreData) {
      autoEndcard = new LastChanceEndscreen(
        appStoreData,
        this.snippetEl,
        (e) => {
          Madlib.handleNavigationClick(e, {
            ae: 1,
          });
        },
        this.bidContext.exchange === "inmobi",
        this.env
      );
    }

    this.useAutoendcardBehaviour =
      this.bidContext.last_chance_endscreen_autoclick_behavior;

    // If exchange is mintegral then only display the auto endcard on delay if
    // platform is ios.
    if (
      this.useAutoendcardBehaviour &&
      (this.bidContext.exchange != "mintegral" ||
        this.bidContext.platform === "ios")
    ) {
      this._lastChanceEndscreenAutoclick = new Autoclick(
        this.useAutoendcardBehaviour,
        {
          clickthroughHandler: () => {
            if (autoEndcard) {
              autoEndcard.initAutoEndcard();
            }
          },
        }
      );
      this._autoclickBus.push(this._lastChanceEndscreenAutoclick);
    }

    this._lastChanceEndscreenInteractionAutoclick = new Autoclick(
      this.bidContext.last_chance_endscreen_interaction_autoclick_behavior,
      {
        clickthroughHandler: () => {
          if (autoEndcard) {
            autoEndcard.initAutoEndcard();
          }
        },
      }
    );
    this._autoclickBus.push(this._lastChanceEndscreenInteractionAutoclick);
    /**
     * Handle cases where the interaction event is triggered from the
     * "tresensa-snippet" Madlib component.
     */
    window.addEventListener(
      "message",
      (e) => {
        onTreSensaInteraction(
          e,
          autoEndcard,
          this._lastChanceEndscreenAutoclick,
          this._lastChanceEndscreenInteractionAutoclick,
          this.vxInteractionListeners
        );
      },
      true
    );
  }

  static setInteractionPromptReached(e) {
    this._autoclickBus.forEach((ac) => ac.emitInteractionPromptReachedEvent(e));
  }

  static getAnimationDurationLimit() {
    return this.env.creative_policy.animation_duration_limit;
  }

  static handleMadlibError(e) {
    Logger.recordError(e);
  }

  static hasTestGroup(group) {
    return !!this.testGroups[group];
  }

  static isExchange(exchange) {
    return this.bidContext.exchange.toLowerCase() === exchange.toLowerCase();
  }

  static updateMRAID() {
    if (this.bidContext.is_interstitial) {
      const { width, height } = this.bidContext;
      LiftoffMRAID.lockOrientation(width < height ? "portrait" : "landscape");
    }
  }

  static loadLogger() {
    const {
      enable_logging: enableLogging,
      enable_html_return: enableHtmlReturn,
      logging_endpoint: endpoint,
      event_tracking_param: trackingParam,
      html_return_delay: delay,
    } = this.env;

    Logger.passContext(
      endpoint,
      trackingParam,
      getAdexpContextSnapshotFn(this.snippetEl, () => {
        return LiftoffMRAID.hasSupport("mraid");
      })
    );

    if (!enableLogging) {
      return;
    }

    Logger.enable();
    Logger.addErrorListeners();

    if (!enableHtmlReturn) {
      return;
    }
    CreativeUtils.configureHTMLReturn(endpoint, delay);
  }

  static loadDOM() {
    this.snippetEl = document.createElement("div");
    this.snippetEl.id = "liftoff-snippet";
    this.snippetEl.classList.add(this.bidContext.platform.toLowerCase());
    this.snippetEl.classList.add(...this.bidContext.ab_tests);
    document.body.appendChild(this.snippetEl);
    // TreSensa endcards using the adcontainer.js library rely on the
    // "tre_container" div being present.
    if (this.env.is_tre) {
      this.treContainerEl = document.createElement("div");
      this.treContainerEl.id = "tre_container";
      this.treContainerEl.zIndex = 2;
      this.snippetEl.appendChild(this.treContainerEl);
    }
  }

  /**
   * A creative policy is a collection of special requirements the creative must comply with. These tend
   * to be country and/or exchange specific.
   * Creative policies are set in admarkup/core.
   * @param {Object} policy - A series of requirements.
   * @param {boolean} policy.add_border - Adds a border around the creative.
   * @param {string} policy.russian_age_rating - Shows age rating watermark.
   * @param {number} policy.animation_duration_limit - Stops all animation after a set time.
   * @param {boolean} policy.show_chinese_watermark - Shows Chinese watermark.
   * @param {boolean} policy.show_ad_choices - Shows the AdChoices watermark.
   * @param {boolean} policy.block_notifications - Blocks notification icons from appearing on chat banners.
   * @param {boolean} policy.block_strobing - Keeps strobing/blinking animations from happening.
   * @param {boolean} policy.use_custom_close - True iff we should use a custom close button for this
   *                                            impression.
   * @param {number} policy.close_delay - Overrides any previously set close delay duration.
   */
  static applyPolicy(
    {
      add_border,
      russian_age_rating,
      show_chinese_watermark,
      show_ad_choices,
      use_custom_close,
      close_delay,
    } = this.env.creative_policy
  ) {
    const bidContext = this.bidContext;
    CreativeUtils.applyLiftoffPolicy(
      this.snippetEl,
      {
        addBorder: add_border,
        showAdChoices: show_ad_choices,
        russianAgeRating: russian_age_rating,
        showChineseWatermark: show_chinese_watermark,
        useCustomClose: use_custom_close,
        closeDelay: close_delay,
        // NOTE(Casey): `applyLiftoffPolicy` globally modifies audio output of the entire creative. Currently,
        // madlib creatives do not use these global controls -- we depend on components to directly check
        // the audio policy before making sound. We therefore set `audioPolicy` here to `NO_RESTRICTION` to
        // disable the global controls.
        // TODO(Casey): Update this if we decide to use global controls for madlib creatives in the future.
        audioPolicy: AUDIO_POLICY_NO_RESTRICTIONS,
      },
      {
        creativeID: bidContext.creative_id,
        platform: bidContext.platform,
        exchange: bidContext.exchange.toLowerCase(),
        sourceAppName: bidContext.source_app_name,
        sourceAppID: bidContext.source_app_id,
        cdnURL: bidContext.cdnURL,
      }
    );
  }

  static onMadlibLoaded() {
    this.madlibLoaded = true;
  }

  static resetClickParams() {
    this.clickParams = {};
  }

  static mergeClickParams(params) {
    Object.assign(this.clickParams, params);
  }

  static getClickParams() {
    return this.clickParams;
  }

  /** Handles clickthrough/CTA clicks. */
  static handleClick(queryParams = {}, clickAreaType = null) {
    /**
     * NOTE(oscar): To prevent aggressive behavior, we disable autoclick the
     * first time the store page is opened regardless of how.
     * mac=1 means there will be no UX change.
     */
    if (this._lastChanceEndscreenAutoclick && !queryParams.mac) {
      this._lastChanceEndscreenAutoclick.disable();
    }
    // NOTE(oscar): pv=1 clicks should mean the MMP is not sent a click.
    if (!queryParams.pv) {
      this._viewclick.disable();
    }

    const clickTrackingParams = ClickHandling.getClickTrackingQueryParams(
      this.interactionMonitor.getAllInteractionData()
    );

    const allQueryParams = {
      cbl: this.madlibLoaded ? 0 : 1,
      ...this.getClickParams(),
      ...clickTrackingParams,
      ...queryParams,
    };

    if (clickAreaType != null) {
      allQueryParams.ca = clickAreaType;
    }

    const {
      bidContext: bidContext,
      env: { click_config: clickConfig },
    } = this;
    ClickHandling.handleClick({
      clickConfig: {
        clickthroughURL: clickConfig.clickthrough_url,
        clickTrackingURLs: clickConfig.click_tracking_urls,
      },
      queryParams: allQueryParams,
      isReengagement: bidContext.is_reengagement,
      platform: bidContext.platform,
      exchange: bidContext.exchange,
      abTests: bidContext.ab_tests,
    });
  }

  static onClickCapture(e) {
    // An array of EventTarget objects, some of which may be instances of Element.
    const path = (e.composedPath && e.composedPath()) || e.path;
    if (path == null) {
      return;
    }

    const foundSnippet = path.some((el) => el === this.snippetEl);
    if (!foundSnippet) {
      return;
    }

    const rect = this.snippetEl.getBoundingClientRect();
    const clickMetric = {
      x: Math.round(e.pageX - rect.left),
      y: Math.round(e.pageY - rect.top),
      time_ms: Date.now() - LIFTOFF_START_TS,
      // NOTE(vsingh): The click type below must be one of AdexpMetric.ClickType.
      type: CLICK_METRIC_TYPE_UNKNOWN,
    };

    // Helper function that returns true if some Elements in `path` satisfy the condition `pred`.
    const someElements = (pred) =>
      path.some(
        (eventtarget) => eventtarget instanceof Element && pred(eventtarget)
      );

    if (someElements((el) => el.classList.contains("liftoff-close-target"))) {
      clickMetric.type = CLICK_METRIC_TYPE_CLOSE;
    } else if (pointInCTA(e.clientX, e.clientY)) {
      clickMetric.type = CLICK_METRIC_TYPE_CTA;
    } else if (
      someElements((el) => el.classList.contains("liftoff-deadzone"))
    ) {
      clickMetric.type = CLICK_METRIC_TYPE_DEADZONE;
    } else if (someElements((el) => el.id === "liftoff-close")) {
      // NOTE(vsingh): Special/weird case: if we hit liftoff-close but not liftoff-close-target,
      // consider it a deadzone click. There are just a few pixels around the close button that
      // trigger this `if` clause.
      clickMetric.type = CLICK_METRIC_TYPE_DEADZONE;
    }

    Logger.recordMetricV2("click", clickMetric);
    this.lastClickMetric = clickMetric;
  }

  static addEventListeners() {
    LiftoffMRAID.onViewable(() => {
      Logger.recordMetricV2("first_viewable_ms", Date.now() - LIFTOFF_START_TS);
    });
    LiftoffMRAID.onViewable((e) => {
      this._autoclickBus.forEach((ac) => ac.emitViewableEvent(e));
    }, true);
    LiftoffMRAID.onViewableChange((e) => {
      this._autoclickBus.forEach((ac) => ac.emitViewableChangeEvent(e));
    });

    window.addEventListener(
      "click",
      (e) => {
        this.onClickCapture(e);
        if (this._lastChanceEndscreenAutoclick) {
          this._lastChanceEndscreenAutoclick.disable();
        }
      },
      true
    );
  }

  static onLoadCompleted() {
    // NOTE(oscar): We log the initial dimensions immediately in case no "resize" event triggers.
    this.windowResizeLogger.logInitialDims();
  }

  static onFirstLayoutFinished() {
    Logger.recordMetricV2("load_time_ms", Date.now() - LIFTOFF_START_TS);
    this._autoclickBus.forEach((ac) => ac.emitHtmlLoadedEvent());
  }

  static getLetterboxConfig() {
    return new LetterboxConfig(this.snippetEl);
  }

  static resolveAsset(assetType, assetID) {
    const asset = this.assets[`${assetType}s`][assetID];
    if (asset == null) {
      throw new Error(`${assetType}: ${assetID} not found`);
    }

    switch (assetType) {
      // NOTE(oscar): By default, we should assume only 1 asset URL per original will be passed to the markup.
      // Occasionally, we will test how we serve our assets, which may entail providing more than 1 version to
      // be selected here.

      // The common structure for an original asset is as follows:
      /**
       * {
       *  "id",
       *  "asset-versions": [{ "public-url", "version-id" }, ...]
       * }
       */
      case "image_original":
        return asset.image_versions[0]["public-url"];
      case "video_original":
        return asset.video_versions[0]["public-url"];
    }

    return asset.url;
  }

  /**
   * Enables exclusive format creatives to listen for Madlib interaction events.
   * Note: This method does not register interaction events for Madlib CSPs.
   */
  static addVXInteractionEventListener(cb) {
    this.vxInteractionListeners.push(cb);
  }
}

Object.assign(Liftoff, {
  Component,
  DOMUtils,
  LiftoffMRAID,
  Logger,
  Utils,
});

window.Liftoff = Liftoff;



// WEBPACK FOOTER //
// ./src/js/madlib/liftoff.js